import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Space, Table, Tag } from 'antd';
import { ColumnsType, FilterValue } from 'antd/es/table/interface';

import Auth0RolesContext from '@totem/components/administration/auth0/roles/Auth0RolesContext';
import { IBAuth0Role } from '@totem/components/administration/auth0/roles/types';
import { isNotNull } from '@totem/utilities/common';

const EMPTY_ID = '000000000000000000000000';

const displayRef = (id?: string, name?: string) => {
  if (!id || id === EMPTY_ID || !name) return '';
  return name;
};

const Auth0RolesTable = () => {
  const { data, loading, onEdit } = useContext(Auth0RolesContext);
  const [nameSearch, setNameSearch] = useState('');
  // Antd requires every filterable column to be either controlled
  // (filteredValue) or uncontrolled — mixing the two makes antd silently
  // discard the other columns' filter selections on every render, so the
  // filter icon never lights up and the rows never actually filter.
  // Organization/Building/Status/Top Level are lifted into this shared
  // controlled state via the Table's onChange to keep them consistent.
  const [filteredInfo, setFilteredInfo] = useState<
    Record<string, FilterValue | null>
  >({});

  const orgFilters = useMemo(
    () =>
      Array.from(
        new Map(
          data
            .filter(
              (rec) =>
                rec.hierarchyReference?.organization?.id &&
                rec.hierarchyReference.organization.id !== EMPTY_ID,
            )
            .map((rec) => [
              rec.hierarchyReference.organization.id,
              rec.hierarchyReference.organization.name,
            ]),
        ).entries(),
      )
        .sort((aEntry, bEntry) => aEntry[1].localeCompare(bEntry[1]))
        .map(([id, name]) => ({ text: name, value: id })),
    [data],
  );

  const buildingFilters = useMemo(
    () =>
      Array.from(
        new Map(
          data
            .filter(
              (rec) =>
                rec.hierarchyReference?.building?.id &&
                rec.hierarchyReference.building.id !== EMPTY_ID,
            )
            .map((rec) => [
              rec.hierarchyReference.building.id,
              rec.hierarchyReference.building.name,
            ]),
        ).entries(),
      )
        .sort((aEntry, bEntry) => aEntry[1].localeCompare(bEntry[1]))
        .map(([id, name]) => ({ text: name, value: id })),
    [data],
  );

  const statusFilters = useMemo(
    () =>
      Array.from(new Set(data.map((rec) => rec.status)))
        .sort()
        .map((sts) => ({ text: sts, value: sts })),
    [data],
  );

  const rows = useMemo(() => {
    const nameTerm = nameSearch.trim().toLowerCase();

    if (!nameTerm) {
      return data;
    }

    return data.filter((rec) => rec.name.toLowerCase().includes(nameTerm));
  }, [data, nameSearch]);

  const columns: ColumnsType<IBAuth0Role> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => {
        return (
          <Link
            to={`/dashboard/remoteAccess/auth0role/${record.id}?from=auth0roles::`}
          >
            {record.name}
          </Link>
        );
      },
      sorter: (aRow, bRow) => aRow.name.localeCompare(bRow.name),
    },
    {
      title: 'Description',
      key: 'description',
      render: (_, record) => record.role?.description ?? '',
    },
    {
      title: 'Organization',
      key: 'organization',
      filters: orgFilters,
      filteredValue: filteredInfo.organization ?? null,
      onFilter: (value, record) =>
        record.hierarchyReference?.organization?.id === value,
      render: (_, record) =>
        displayRef(
          record.hierarchyReference?.organization?.id,
          record.hierarchyReference?.organization?.name,
        ),
      sorter: (aRow, bRow) =>
        (aRow.hierarchyReference?.organization?.name ?? '').localeCompare(
          bRow.hierarchyReference?.organization?.name ?? '',
        ),
    },
    {
      title: 'Region',
      key: 'region',
      render: (_, record) =>
        displayRef(
          record.hierarchyReference?.region?.id,
          record.hierarchyReference?.region?.name,
        ),
      sorter: (aRow, bRow) =>
        (aRow.hierarchyReference?.region?.name ?? '').localeCompare(
          bRow.hierarchyReference?.region?.name ?? '',
        ),
    },
    {
      title: 'Building',
      key: 'building',
      filters: buildingFilters,
      filteredValue: filteredInfo.building ?? null,
      onFilter: (value, record) =>
        record.hierarchyReference?.building?.id === value,
      render: (_, record) => {
        if (
          isNotNull(record.hierarchyReference) &&
          isNotNull(record.hierarchyReference?.building) &&
          record.hierarchyReference.building.id !== '' &&
          record.hierarchyReference.building.id !== EMPTY_ID
        ) {
          return (
            <Link
              to={`/dashboard/buildings/${record.hierarchyReference?.building?.id}`}
            >
              {' '}
              {displayRef(
                record.hierarchyReference?.building?.id,
                record.hierarchyReference?.building?.name,
              )}
            </Link>
          );
        }

        return (
          <span>
            {displayRef(
              record.hierarchyReference?.building?.id,
              record.hierarchyReference?.building?.name,
            )}
          </span>
        );
      },
      sorter: (aRow, bRow) =>
        (aRow.hierarchyReference?.building?.name ?? '').localeCompare(
          bRow.hierarchyReference?.building?.name ?? '',
        ),
    },
    {
      title: 'Control System',
      key: 'controlSystem',
      render: (_, record) =>
        displayRef(
          record.hierarchyReference?.controlSystem?.id,
          record.hierarchyReference?.controlSystem?.name,
        ),
      sorter: (aRow, bRow) =>
        (aRow.hierarchyReference?.controlSystem?.name ?? '').localeCompare(
          bRow.hierarchyReference?.controlSystem?.name ?? '',
        ),
    },
    {
      title: 'Top Level',
      key: 'isTopLevel',
      filters: [{ text: 'Yes', value: true }],
      filteredValue: filteredInfo.isTopLevel ?? null,
      onFilter: (value, record) => record.isTopLevel === value,
      render: (_, record) =>
        record.isTopLevel ? <Tag color="blue">Yes</Tag> : null,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: statusFilters,
      filteredValue: filteredInfo.status ?? null,
      onFilter: (value, record) => record.status === value,
      sorter: (aRow, bRow) => aRow.status.localeCompare(bRow.status),
      render: (sts: string) => (
        <Tag color={sts === 'Active' ? 'success' : 'default'}>{sts}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEdit(record)}>
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Space wrap size="middle" style={{ marginBottom: 16 }}>
        <Input
          placeholder="Role Name Search"
          value={nameSearch}
          allowClear
          onChange={(evt) => setNameSearch(evt.target.value)}
          style={{ width: 220 }}
        />
      </Space>
      <Table<IBAuth0Role>
        columns={columns}
        dataSource={rows}
        loading={loading}
        rowKey="id"
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        onChange={(_pagination, filters) => setFilteredInfo(filters)}
      />
    </>
  );
};

export default Auth0RolesTable;
